import { useState, useRef, useEffect } from 'react';
import { X, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CPV_CODES } from '../../constants/cpv-codes';

interface CpvCodeMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  maxDisplayed?: number;
}

export function CpvCodeMultiSelect({
  value,
  onChange,
  placeholder = 'Rechercher des codes CPV...',
  maxDisplayed = 3,
}: CpvCodeMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredCodes = CPV_CODES.filter(
    (cpv) =>
      cpv.code.includes(search) ||
      cpv.label.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleCode = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter((v) => v !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const removeCode = (code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== code));
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto min-h-10 px-3 py-2"
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {value.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              <>
                {value.slice(0, maxDisplayed).map((code) => (
                  <Badge key={code} variant="secondary" className="mr-1 mb-1">
                    {code.substring(0, 8)}
                    <button
                      type="button"
                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={(e) => removeCode(code, e)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {value.length > maxDisplayed && (
                  <Badge variant="outline" className="mr-1 mb-1">
                    +{value.length - maxDisplayed}
                  </Badge>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-1 ml-2">
            {value.length > 0 && (
              <button
                type="button"
                className="p-1 hover:bg-muted rounded"
                onClick={clearAll}
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            ref={inputRef}
            placeholder="Rechercher par code ou libellé..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
          />
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {filteredCodes.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Aucun code CPV trouvé
            </div>
          ) : (
            filteredCodes.map((cpv) => {
              const isSelected = value.includes(cpv.code);
              return (
                <button
                  key={cpv.code}
                  type="button"
                  onClick={() => toggleCode(cpv.code)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer',
                    'hover:bg-accent hover:text-accent-foreground',
                    isSelected && 'bg-accent',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-4 w-4 items-center justify-center rounded-sm border border-primary shrink-0',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50',
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 font-mono">
                    {cpv.code}
                  </span>
                  <span className="flex-1 text-left truncate">{cpv.label}</span>
                </button>
              );
            })
          )}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t text-xs text-muted-foreground">
            {value.length} code{value.length > 1 ? 's' : ''} CPV sélectionné
            {value.length > 1 ? 's' : ''}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
